import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import "./TreeVisualization.css";
import { 
  Box, 
  Flex,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useToast
} from "@chakra-ui/react";
import { 
  TreeNode, 
  TreeConfig, 
  D3TreeNode,
  calculateNodeHeight,
  getPathToRoot,
  connector,
  applyCollapsedStates,
  generateNodeHTML,
  findNewNode
} from "../../utils/treeUtils";
import { useChat } from "context/ChatBotContext";
import { useAnalysisTreeContext } from "../../context/AnalysisTreeContext";
import { StatefulRequestBuilder } from "../../containers/stateful/StatefulRequests";
import { statefulAnalysisTree } from "../../services/AnalysisTreeServices";
import { getErrorMsg } from "./data_table/dataTableUtils";

/** Props for the TreeVisualization component
 * @param data - The tree data structure to visualize, from TreeNode interface
 * @param onNodeClick - Callback function when a node is clicked
 *                      Takes the node ID and whether shift key was pressed
 * @param isChatOpen - Flag indicating if the chat panel is open
 * @param activeDataTables - Map of node IDs to their associated table indices
 */
interface TreeVisualizationProps {
  data: TreeNode;
  onNodeClick: (nodeId: string, shiftKey: boolean) => void;
  isChatOpen: boolean;
  activeDataTables: Map<string, number>;
  isClearAllTables: boolean;
  setIsClearAllTables: (isClearAllTables: boolean) => void;
}

/**
 * Typed D3-based tree with collapsible nodes with selection.
 */
const TreeVisualization: React.FC<TreeVisualizationProps> = ({ data, onNodeClick, isChatOpen, activeDataTables, isClearAllTables, setIsClearAllTables }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const rootRef = useRef<D3TreeNode | null>(null);
  const prevDataRef = useRef<TreeNode | null>(null);
  const initialSelectionDoneRef = useRef<boolean>(false);
  const { referencedNodeId } = useChat();
  const { transactionId, nodeInfoMap } = useAnalysisTreeContext();

  const [config, setConfig] = useState<TreeConfig>({
    linkStrength: 27,
    linkHeight: 33,
  });

  // Track the currently selected node's ID
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Track the path of IDs from root to selected node
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  // Map of node IDs to their collapsed state (true = collapsed)
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>({});

  // Reference to the container div for measuring available space
  const containerRef = useRef<HTMLDivElement>(null);

  // Dynamic height that will be calculated based on content
  let height = 0;

  // Background colors using Chakra's color mode
  const bgColor = useColorModeValue("bg.surface", "gray.800");

  // Add state for editing
  const [editingNode, setEditingNode] = useState<D3TreeNode | null>(null);
  const [editedName, setEditedName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // function to handle node label edit
  const handleEditClick = (event: React.MouseEvent, node: D3TreeNode) => {
    event.stopPropagation(); // Stop event from bubbling up
    
    // Set the editing node and current name
    setEditingNode(node);
    setEditedName(node.data.name || "");
    
    // Open the modal
    onOpen();
  };

  // function to save the edited node label
  const handleSaveEdit = async () => {
    if (!editingNode) return;
    
    try {
      // Create the update request
      const requestBuilder = new StatefulRequestBuilder(transactionId);
      const request = requestBuilder.UpdateNodeRequest({
        node_id: editingNode.data.id,
        new_node_label: editedName
      });

      // Send the request
      await statefulAnalysisTree(request);
      
      editingNode.data.name = editedName;
      
      // Force a tree update to reflect changes
      if (rootRef.current) {
        update(rootRef.current);
      }
      
      onClose();
      toast({
        title: "Node Label Updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to Update Node Label",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Add a function to force a complete tree update
  const forceTreeUpdate = () => {
    if (rootRef.current) {
      // Force a complete recalculation and redraw
      const root = rootRef.current;
      update(root);
    }
  };

  // Function to get available width
  const getAvailableWidth = () => {
    if (!containerRef.current) return 600;
    const totalWidth = containerRef.current.offsetWidth;
    const baseMargin = Math.min(50, totalWidth * 0.05); // 5% of width or 50px max
    const leftMargin = baseMargin;
    const rightMargin = isChatOpen ? baseMargin : baseMargin;
    let availableWidth = Math.max((totalWidth - leftMargin - rightMargin), 250) * (isChatOpen ? 0.85 : 1);
    if (totalWidth > 1200) {
      availableWidth = Math.min(availableWidth, 800);
    }
    return availableWidth;
  };

  /**
   * The main D3 update function that:
   * 1. Applies collapsed/expanded states to nodes
   * 2. Calculates node positions and spacing
   * 3. Draws connecting links between nodes
   * 4. Creates and updates node elements (circles and text labels)
   * 5. Handles transitions and animations
   * 6. Updates node selection and path highlighting
   */
  function update(source: D3TreeNode) {
    if (!svgRef.current) return;

    // 1. COLLAPSED STATES
    // Apply collapsed/expanded states to ensure tree structure is consistent
    applyCollapsedStates(source, collapsedNodes);

    // 2. CALCULATE POSITIONS
    const availableWidth = getAvailableWidth();
    const svg = d3.select(svgRef.current);
    const g = svg.select("g"); // The <g> container created in useEffect

    // Build a d3 tree layout with specified node spacing
    const treeLayout = d3.tree<TreeNode>().nodeSize([0, config.linkStrength]);

    // Convert hierarchy to point-based layout
    const rootPoint = treeLayout(source);

    // Get flat array of nodes for manual spacing calculations
    const allNodes: D3TreeNode[] = [];
    rootPoint.eachBefore((node) => {
      allNodes.push(node);
    });

    // Calculate vertical positions with dynamic spacing
    let yCursor = 0;
    let maxX = 0;
    for (let i = 0; i < allNodes.length; i++) {
      const n = allNodes[i];
      const textHeight = calculateNodeHeight(n.data.name ?? "", availableWidth, isChatOpen);

      if (i === 0) {
        n.x = 0;
      } else {
        const prev = allNodes[i - 1];
        const prevHeight = calculateNodeHeight(prev.data.name ?? "", availableWidth, isChatOpen);
        const minSpacing = Math.max(config.linkHeight, (textHeight + prevHeight) / 2);
        yCursor += minSpacing;
        n.x = yCursor;
      }
      maxX = Math.max(maxX, yCursor);

      // Set horizontal position based on depth
      n.y = (n.depth ?? 0) * config.linkStrength;
    }

    // Update SVG dimensions based on content
    const dynamicHeight = maxX + config.linkHeight * 2;
    height = dynamicHeight;
    svg.attr("height", dynamicHeight).style("min-height", `${dynamicHeight}px`);

    // 3. DRAW CONNECTING LINKS
    const links = allNodes.slice(1); // Skip root node
    const linkSel = g.selectAll<SVGPathElement, D3TreeNode>("path.link").data(links, (d) => {
      const childId = d.data.id;
      const parentId = d.parent ? (d.parent as D3TreeNode).data.id : "";
      return `${childId}->${parentId}`;
    });

    // Helper for link styling classes
    const getLinkClasses = (d: D3TreeNode) => {
      const classes = ["link"];
      if ((d.parent as D3TreeNode)?.isRoot) classes.push("root-link");
      if (d.data.id && d.parent && (d.parent as D3TreeNode).data.id && selectedPath.includes(d.data.id) && selectedPath.includes((d.parent as D3TreeNode).data.id)) {
        classes.push("selected-path");
      }
      return classes.join(" ");
    };

    // Handle entering links
    const linkEnter = linkSel
      .enter()
      .insert("path", "g")
      .attr("class", getLinkClasses)
      .attr("d", (d) => {
        const o = { x: source.x0, y: source.y0 };
        return connector({ ...d, parent: { x: o.x, y: o.y } });
      });

    // Update existing links
    linkSel.attr("class", getLinkClasses);

    // Transition links to new positions
    linkSel
      .merge(linkEnter as any)
      .transition()
      .duration(0)
      .attr("d", connector);

    // Remove exiting links with animation
    linkSel
      .exit()
      .transition()
      .duration(0)
      .attr("d", () => {
        const o = { x: source.x, y: source.y, parent: { x: source.x, y: source.y } };
        return connector(o);
      })
      .remove();

    // 4. CREATE/UPDATE NODE ELEMENTS
    const nodeSel = g.selectAll<SVGGElement, D3TreeNode>("g.node").data(allNodes, (d) => d.data.id);

    // Create entering nodes
    const nodeEnter = nodeSel
      .enter()
      .append("g")
      .attr("class", (d) => ["node"].join(" "))
      .attr("data-id", (d) => d.data.id)
      .attr("transform", (d) => `translate(${d.y},${d.x})`);

    // 5. HANDLE TRANSITIONS AND INTERACTIONS
    // Add click handlers and visual elements
    nodeEnter.each(function (d) {
      const n = d3.select(this);
      n.append("circle")
        .attr("r", 5)
        .on("click", function (this: SVGCircleElement, event: any, d: unknown) {
          event.stopPropagation();
          const node = d as D3TreeNode;
          setCollapsedNodes((prev) => {
            const newState = { ...prev };
            if (node.children) {
              newState[node.data.id] = true;
            } else {
              delete newState[node.data.id];
            }
            return newState;
          });
        });

      // Add the +/- text
      n.append("text")
        .attr("dy", ".3em")
        .attr("text-anchor", "middle")
        .attr("font-size", "10px")
        .attr("font-weight", "bold")
        .attr("fill", d._children ? "white" : "#4E1E6C") // Set color based on collapsed state
        .attr("pointer-events", "none")
        .text(d._children ? "+" : d.children ? "-" : "");
    });

    // 6. UPDATE SELECTION AND HIGHLIGHTING
    // Create text containers with click handlers
    const textGroups = nodeEnter
      .append("g")
      .attr("class", "text-container")
      .on("click", (event, d) => {
        // Check if click was on edit button
        const target = event.target as HTMLElement;
        if (target.closest("[data-action='edit']")) {
          event.stopPropagation();
          handleEditClick(event, d);
          return;
        }

        // Regular node click handling
        event.stopPropagation();
        onNodeClick(d.data.id, event.shiftKey);
        setSelectedNodeId(d.data.id);
        const pathNodes = getPathToRoot(d);
        const pathIds = pathNodes.map((n) => n.data.id);
        setSelectedPath(pathIds);
      });

    // Helper for text styling based on selection state
    const getTextStyles = (d: D3TreeNode | undefined) => {
      if (!d || !d.data) {
        return {
          fontWeight: "500",
          color: "var(--chakra-colors-gray-700)",
        };
      }
      return {
        fontWeight: d.data.id === selectedNodeId ? "500" : selectedPath.includes(d.data.id) ? "500" : "500",
        color: d.data.id === selectedNodeId || selectedPath.includes(d.data.id) ? "var(--chakra-colors-gray-800)" : "var(--chakra-colors-gray-700)",
      };
    };

    // Update existing nodes and text
    const nodeUpdate = nodeSel.merge(nodeEnter as any);
    
    // Update the +/- text
    nodeUpdate
      .select("text")
      .attr("fill", d => d._children ? "white" : "#4E1E6C")
      .text(d => d._children ? "+" : d.children ? "-" : "");

    nodeUpdate
      .selectAll("foreignObject div")
      .style("font-weight", (d: any) => getTextStyles(d as D3TreeNode).fontWeight)
      .style("color", (d: any) => getTextStyles(d as D3TreeNode).color)
      .style("font-size", isChatOpen ? "0.8rem" : "0.95rem")
      .html((d: any) => {
        if (!d) return "<div>Loading...</div>";
        const node = d as D3TreeNode;
        const nodeId = node.data.id;
        const text = node.data.name || "Unnamed";
        const tableIndex = activeDataTables.get(nodeId);
        const errorMsg = getErrorMsg(nodeId, nodeInfoMap!);
        return generateNodeHTML(text, tableIndex, isChatOpen, availableWidth, nodeId === selectedNodeId, errorMsg);
      });

    textGroups
      .append("foreignObject")
      .attr("x", 10)
      .attr("y", (d) => {
        const nodeName = d.data?.name || d.data?.observation?.header?.name || "Unnamed";
        const h = calculateNodeHeight(nodeName, availableWidth, isChatOpen);
        return -h / 2;
      })
      .attr("width", availableWidth)
      .attr("height", (d) => {
        const nodeName = d.data?.name || "Unnamed";
        return calculateNodeHeight(nodeName, availableWidth, isChatOpen) + 20;
      })
      .append("xhtml:div")
      .style("font-weight", (d: any) => getTextStyles(d as D3TreeNode).fontWeight)
      .style("color", (d: any) => getTextStyles(d as D3TreeNode).color)
      .style("font-size", isChatOpen ? "0.8rem" : "0.95rem")
      .html((d) => {
        if (!d || !d.data) return "<div>Loading...</div>";
        const nodeId = d.data.id;
        const text = d.data.name || "Unnamed";
        const tableIndex = activeDataTables.get(nodeId);
        return generateNodeHTML(text, tableIndex, isChatOpen, availableWidth);
      });

    // Animate node transitions
    nodeSel
      .merge(nodeEnter as any)
      .transition()
      .duration(0)
      .attr("transform", (d) => `translate(${d.y},${d.x})`);

    // Update circle styles
    nodeSel
      .merge(nodeEnter as any)
      .select("circle")
      .attr("class", (d) => {
        const classes = [];
        if (d._children) classes.push("collapsed");
        if (d.data.id === selectedNodeId) classes.push("selected");
        return classes.join(" ");
      })
      .attr("cursor", "pointer");

    // Clean up removed nodes
    nodeSel.exit().remove();

    // Store positions for next update
    allNodes.forEach((n) => {
      n.x0 = n.x;
      n.y0 = n.y;
    });
  }

  // Initialize SVG and root hierarchy
  useEffect(() => {
    if (!svgRef.current) return;

    const margin = { top: 20, right: 20, bottom: 50, left: 20 };
    const svg = d3
      .select(svgRef.current)
      .attr("width", "100%")
      .attr("height", height + margin.top + margin.bottom);

    svg.append("g")
      .attr("class", "main-group")
      .attr("transform", `translate(${margin.left},${margin.top})`);
  }, []);

  // Update the data effect to handle new node selection
  useEffect(() => {
    if (!data || !svgRef.current) return;

    const hierarchy = d3.hierarchy(data);
    const root = hierarchy as D3TreeNode;
    root.x0 = 0;
    root.y0 = 0;
    root.isRoot = true;
    rootRef.current = root;

    const newNodeId = findNewNode(data, prevDataRef.current);
    if (newNodeId) {
      setSelectedNodeId(newNodeId);
      const newNode = root.find((n) => n.data.id === newNodeId);
      if (newNode) {
        const pathNodes = getPathToRoot(newNode as D3TreeNode);
        const pathIds = pathNodes.map((n) => n.data.id);
        setSelectedPath(pathIds);
      }
    }

    prevDataRef.current = data;
    update(root);
  }, [data, isChatOpen, config, selectedNodeId, selectedPath, collapsedNodes, activeDataTables]);

  // Handle updates when selection state changes
  useEffect(() => {
    if (rootRef.current) {
      update(rootRef.current);
    }
  }, [selectedNodeId, selectedPath, collapsedNodes, config, isChatOpen]);

  //UseEffect to set the selected node id when a node is clicked from the chat so that it is highlighted in the tree
  useEffect(() => {
    if (referencedNodeId?.nodeId) {
      setSelectedNodeId(referencedNodeId.nodeId);
    }
  }, [referencedNodeId]);

  // UseEffect to automatically select the root node when the tree first loads
  useEffect(() => {
    if (data && !initialSelectionDoneRef.current && rootRef.current) {
      // Get the root node ID
      const rootNodeId = data.id;
      
      setSelectedNodeId(rootNodeId);
      
      setSelectedPath([rootNodeId]);
      
      // Mark that we've done the initial selection
      initialSelectionDoneRef.current = true;
      
      // Force update to apply the selection visually
      forceTreeUpdate();
    }
  }, [data, rootRef.current]);

  // Reset the initial selection flag when the tree changes
  useEffect(() => {
    if (prevDataRef.current?.id !== data?.id) {
      initialSelectionDoneRef.current = false;
    }
  }, [data?.id]);

  // UseEffect to clear the highlight of the node when the all-tables view is cleared
  useEffect(() => {
      // Clear selection state
      if(isClearAllTables===true) {
        setSelectedNodeId(null);
        setSelectedPath([]);
        
      // Force update to apply changes
      forceTreeUpdate();
      setIsClearAllTables(false);
    }
  }, [isClearAllTables]);

  return (
    <Flex direction="row" w="100%" h="100%" p={3} ref={containerRef}>
      <Box
        flex="1"
        bg={bgColor}
        overflowX="hidden"
        overflowY="auto"
        position="relative"
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <Box className="tree-container" w="100%" minH="100%" position="relative" pr={4}>
          <svg ref={svgRef} width="100%" style={{ display: "block", minHeight: "100%" }} />
        </Box>
      </Box>

      {/* Modal to Edit Node Label*/}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Node Label</ModalHeader>
          <ModalBody>
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Enter new node label"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSaveEdit}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default TreeVisualization;